/**
 * What the PIN lock remembers on this phone, per person (FM Technician B17,
 * review R2-1). No imports, so the auth store can clear it on sign-in and
 * sign-out without an import cycle.
 *
 *  - `tech-locked:<uid>`      '1' while the lock is up (a reload is no way past it)
 *  - `tech-pin-set:<uid>`     '1' / '0' — the last pin_set the server told us,
 *                              so the lock can come up after a reload BEFORE
 *                              GET /tech/profile answers, and offline
 *  - `tech-last-active:<uid>` the last touch (ms), so "15 minutes away" also
 *                              counts the time the page was closed or reloaded
 *
 * Storage refused (private mode): the values live in memory for this page.
 */
import { useSyncExternalStore } from 'react';

/** Interpretation: 15 minutes without a touch locks the app. */
export const LOCK_AFTER_MS = 15 * 60 * 1000;

const lockKey = (uid: string) => `tech-locked:${uid}`;
const pinKey = (uid: string) => `tech-pin-set:${uid}`;
const activeKey = (uid: string) => `tech-last-active:${uid}`;

const memory = new Map<string, string>();
function read(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return memory.get(k) ?? null; }
}
function write(k: string, v: string | null): void {
  if (v === null) memory.delete(k); else memory.set(k, v);
  try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* memory only */ }
}

const listeners = new Set<() => void>();
function emit(): void { listeners.forEach((l) => l()); }

/**
 * Review R2-2: ANOTHER TAB's changes count too. The lock, the PIN flag and the
 * last touch are shared by every tab of this phone (localStorage), and the
 * browser tells the other tabs with a `storage` event — so an unlock in one
 * tab unlocks the others, and a lock in one locks them.
 */
const OURS = /^tech-(locked|pin-set|last-active):/;
function onStorage(e: StorageEvent): void {
  if (e.key === null || OURS.test(e.key)) emit();
}
/** Told whenever the lock state may have changed, in this tab or another. */
export function onLockChange(l: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  listeners.add(l);
  return () => {
    listeners.delete(l);
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** The lock flag as stored ('1' while the lock is up). */
export function lockFlagSet(uid: string | null | undefined): boolean {
  return !!uid && read(lockKey(uid)) === '1';
}

/**
 * Is the lock up for this person — worked out NOW, at render time (review
 * R2-2): the stored flag, OR a PIN this phone knows is set with the last
 * touch LOCK_AFTER_MS or more ago. The second half is what stops the page
 * showing for a frame after a long absence, before an effect could set the
 * flag.
 */
export function isLocked(uid: string | null | undefined): boolean {
  if (!uid) return false;
  return lockFlagSet(uid) || (storedPinSet(uid) === true && awayTooLong(uid));
}
export function setLocked(uid: string, locked: boolean): void {
  write(lockKey(uid), locked ? '1' : null);
  if (!locked) markActive(uid);
  emit();
}
/** Re-renders when the lock goes up or down, in this tab or another
 *  (TechMeEffects, AppLayout, the queue toasts). */
export function useTechLocked(uid: string | null | undefined): boolean {
  return useSyncExternalStore(onLockChange, () => isLocked(uid), () => isLocked(uid));
}

/** null = never heard from the server on this phone. */
export function storedPinSet(uid: string): boolean | null {
  const v = read(pinKey(uid));
  return v === null ? null : v === '1';
}
export function rememberPinSet(uid: string, pinSet: boolean): void {
  if (storedPinSet(uid) === pinSet) return;
  write(pinKey(uid), pinSet ? '1' : '0');
  emit();
}

export function lastActive(uid: string): number | null {
  const v = Number(read(activeKey(uid)));
  return Number.isFinite(v) && v > 0 ? v : null;
}
export function markActive(uid: string, at: number = Date.now()): void { write(activeKey(uid), String(at)); }
/** Away for LOCK_AFTER_MS or more since the last touch (page closed or not). */
export function awayTooLong(uid: string, now: number = Date.now()): boolean {
  const at = lastActive(uid);
  return at !== null && now - at >= LOCK_AFTER_MS;
}

/**
 * A new sign-in, or a sign-out: the lock and the last touch belong to the
 * sign-in that ended, so a password sign-in after a weekend is not met by the
 * lock. pin_set stays — it is a fact about the person, refreshed by the server.
 */
export function clearLockState(uid: string): void {
  write(lockKey(uid), null);
  write(activeKey(uid), null);
  emit();
}
